import * as Cesium from 'cesium';
import { t } from '@/i18n';
import Subscriber from './subscriber';

export default class PointDrawer {
	constructor(viewer, options = {}) {
		if (!viewer) throw new Error('No Cesium viewer provided');
		this._viewer = viewer;

		this._callBack = options.callback;
		this._posChange = options.posChange;
		this._options = Object.assign({ accuracy: 6, once: true, retainGeo: true }, options);

		this._position = null;
		this._pointEntity = null;

		this._tips = Object.assign({ leftClick: t('damages.draw.leftClick') }, options.tips);

		this._labels = this._viewer.scene.primitives.add(new Cesium.LabelCollection());
		this._label = this._labels.add({
			text: this._tips.leftClick,
			font: 'bold 1rem Arial',
			fillColor: Cesium.Color.WHITE,
			backgroundColor: Cesium.Color.BLACK,
			backgroundPadding: new Cesium.Cartesian2(4, 4),
			style: Cesium.LabelStyle.FILL_AND_OUTLINE,
			pixelOffset: new Cesium.Cartesian2(4, 30),
			show: false,
			disableDepthTestDistance: Number.POSITIVE_INFINITY, // Keep label on top
		});

		this._subscriber = new Subscriber(viewer);
		this._events = [];
	}

	// Helper from PolygonDrawer to prevent points clipping into 3D tiles
	applyCameraOffset(position) {
		if (!position) return position;

		// Get camera position
		const cameraPosition = this._viewer.camera.position;

		// Calculate direction vector from point to camera
		const direction = Cesium.Cartesian3.subtract(cameraPosition, position, new Cesium.Cartesian3());

		// Normalize vector
		Cesium.Cartesian3.normalize(direction, direction);

		// Move 0.15m (15cm) towards camera
		const offset = Cesium.Cartesian3.multiplyByScalar(direction, 0.15, new Cesium.Cartesian3());

		// Add to original position
		return Cesium.Cartesian3.add(position, offset, new Cesium.Cartesian3());
	}

	// UPDATED: Generates an SVG Map Pin instead of a Canvas Circle
	createMapPinIcon(color = '#FFFF00') {
		const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"
            fill="none" stroke="${color}" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 21s-6-5.686-6-10a6 6 0 1 1 12 0c0 4.314-6 10-6 10z"/>
            <circle cx="12" cy="11" r="2"/>
        </svg>`;

		return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
	}

	cartesian2lonlat(cart) {
		if (!cart) return [];
		const carto = this._viewer.scene.globe.ellipsoid.cartesianToCartographic(cart);
		const lon = +Cesium.Math.toDegrees(carto.longitude).toFixed(this._options.accuracy);
		const lat = +Cesium.Math.toDegrees(carto.latitude).toFixed(this._options.accuracy);
		const height = +carto.height.toFixed(2); // Include Height for 3D tiles
		return [lon, lat, height];
	}

	handlePosChange() {
		if (!this._posChange) return;
		// Return array to match PolygonDrawer callback structure usually expecting arrays
		const coords = this._position ? this.cartesian2lonlat(this._position) : [];
		this._posChange(coords);
	}

	drawPoint(position) {
		const image = this.createMapPinIcon('#ef4444');
		return this._viewer.entities.add({
			position,
			billboard: {
				image,
				scale: 1.3,
				// UPDATED: Anchors the pin at the bottom (tip) rather than the center
				verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
				// IMPORTANT for 3D Tiles: NONE uses the exact XYZ, CLAMP_TO_GROUND forces it to the floor
				heightReference: Cesium.HeightReference.NONE,
				disableDepthTestDistance: Number.POSITIVE_INFINITY, // Always visible on top
			},
		});
	}

	handleMouseLeftClick = (movement) => {
		const scene = this._viewer.scene;

		// 1. Try picking 3D tile/Mesh
		let cart = scene.pickPosition(movement.position);

		// 2. Fallback to picking ellipsoid (ground)
		if (!cart) {
			cart = this._viewer.camera.pickEllipsoid(movement.position, scene.globe.ellipsoid);
		}

		if (!cart) return;

		// 3. Apply offset to prevent Z-fighting
		cart = this.applyCameraOffset(cart);

		if (this._pointEntity) {
			this._pointEntity.position = cart;
		} else {
			this._pointEntity = this.drawPoint(cart);
		}

		this._position = cart;
		this._label.text = t('damages.draw.clickToUpdatePoint');
		this._label.position = cart;
		this._label.show = true;

		this.handlePosChange();

		if (this._callBack) {
			// Sending as a single coordinate array [lon, lat, height]
			this._callBack(this.cartesian2lonlat(cart));
		}

		scene.requestRender();
	};

	handleMouseMove = (movement) => {
		if (!movement.endPosition) return;

		const scene = this._viewer.scene;

		// 1. Try picking 3D tile
		let cartesian = scene.pickPosition(movement.endPosition);

		// 2. Fallback to ellipsoid
		if (!cartesian) {
			cartesian = this._viewer.camera.pickEllipsoid(movement.endPosition, scene.globe.ellipsoid);
		}

		// Update label position in real-time
		if (cartesian) {
			// Apply offset for the label as well so it doesn't clip
			cartesian = this.applyCameraOffset(cartesian);

			this._label.position = cartesian;
			this._viewer.scene.requestRender();
		}
	};

	start() {
		this.stop();

		// Add mouse move + click event handlers
		this._events = [
			this._subscriber.addExternal(this.handleMouseLeftClick, 'LEFT_CLICK'),
			this._subscriber.addExternal(this.handleMouseMove, 'MOUSE_MOVE'),
		];

		this._label.text = this._tips.leftClick;
		this._label.show = true;
	}

	stop() {
		this._subscriber.removeExternal(this._events);
		this._events = [];
	}

	clear() {
		if (this._pointEntity) {
			this._viewer.entities.remove(this._pointEntity);
			this._pointEntity = null;
		}
		this._label.show = false;
		this._position = null;
		this.stop();
		this.handlePosChange();
		this._viewer.scene.requestRender();
	}

	destroy() {
		this.stop();
		this.clear();
		if (!this._labels.isDestroyed()) {
			this._labels.destroy();
		}
		this._subscriber.destroy();
	}
}
